// pages/admin/AdminRunInspectorPage.tsx — T4 operator UI (PLAN-OPERATOR-UI).
// Enter a run id → see all its steps (status-colored, filterable, grouped by beat)
// with per-step actions (retry/skip/cascade/re-render-beat), run-level actions
// (re-stitch/revive/abandon), an inline artefact viewer per beat, and a
// needs-attention view. Replaces the raw-mysql render-debug loop.
import { CompareExecutions } from "../../components/engine/CompareExecutions";
import { DryRunStopped, RehearsalBadge } from "@/components/engine/DryRun.tsx";   // (tf3-35b; B119)
import { isDryRunStopped, isRehearsal } from "@/lib/dry-run.ts";
import { HOLD_WORDS, HOLD_REMEDY } from "@/lib/waiting.ts";   // (zip 48) a held step named in words
import { useState, useEffect, useCallback, useRef } from "react";
import {
  opsApi, type RunInspector, type RunStep, type NeedsAttentionItem, type BeatProduction, type CompletedJob,
  type Filmstrip, type FilmstripBeat,
} from "../../api/ops.ts";
import FinishAndPostPanel from "./FinishAndPostPanel.tsx";
import { finishingSide } from "../../api/engine-console-rules.ts";   // (session 10, A3: F1 restored)
import { FilmstripView, ContactSheet, type Mark } from "./Filmstrip.tsx";
import { RetentionCurves } from "./RetentionCurves.tsx";
import { AdminNeedsYou } from "../../components/engine/AdminNeedsYou.tsx";   // (TF-6b-1)
import { AdminMaterialRows } from "../../components/engine/AdminMaterialRows.tsx";   // (B112)
import { GateDecideBox } from "../../components/engine/GateDecideBox.tsx";   // (TF-6b-4)
import { AdminGateNotes, AdminScreenplay } from "../../components/engine/GateNotes.tsx";
import { genericDecide } from "@/lib/gate-map.ts";
import { RulesCardPanel } from "../../components/engine/RulesCardPanel.tsx";   // (tf3-38)
import { approveHidden, atStoryboard, rereadDone, REREAD_MS } from "@/lib/rules-card.ts";
import { opsOverviewApi } from "../../api/operations.ts";
import { runEnded, endReasonLine, ABANDON_ASK } from "@/lib/run-end.ts";   // (B105)
import { RunThumbs } from "@/components/RunThumbs.tsx";

const STATUS_COLOR: Record<string, string> = {
  blocked: "#9ca3af", ready: "#2563eb", dispatched: "#7c3aed", running: "#7c3aed",
  // G5: `succeeded` and `awaiting_gate` are the real statuses — neither had a colour, so every succeeded step fell to
  // the fallback. (complete / completed / done are not step statuses; left as they were.)
  succeeded: "#16a34a", awaiting_gate: "#d97706",
  complete: "#16a34a", completed: "#16a34a", done: "#16a34a",
  failed: "#dc2626", skipped: "#d97706",
};
const dur = (s: number | null) => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${s % 60}s`);
const btn: React.CSSProperties = { padding: "4px 9px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 12 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const btnDanger: React.CSSProperties = { ...btn, background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" };
const btnWarn: React.CSSProperties = { ...btn, background: "#fffbeb", color: "#b45309", borderColor: "#fde68a" };

export function AdminRunInspectorPage() {
  const [view, setView] = useState<"attention" | "run" | "completed">("attention");
  const [runId, setRunId] = useState("");
  const [data, setData] = useState<RunInspector | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [beatFilter, setBeatFilter] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<"steps" | "attention">("steps");
  // G5: the run as pictures first — the filmstrip read model, and the reviewer's per-beat marks (working notes, not saved).
  const [film, setFilm] = useState<Filmstrip | null>(null);
  const [filmErr, setFilmErr] = useState<string | null>(null);
  const [marks, setMarks] = useState<Record<string, Mark | undefined>>({});
  // (tf3-38) a rules card edit sends a parked storyboard gate back to waiting: Approve stays hidden until a read started
  //  after the edit shows the gate parked again (or the check held the run again)
  const [edited, setEdited] = useState<{ runId: string; at: number } | null>(null);
  const [readAt, setReadAt] = useState(0);
  const shownRun = useRef<string | null>(null);   // (audit 10-21 F3) the run last opened: a late re-read of another is dropped
  const readSeq = useRef(0);   // (audit 10-23) the latest read: an older read of the same run never lands after it, nor ends its loading

  const load = useCallback(async (id?: string) => {
    const rid = (id ?? runId).trim();
    if (!rid) return;
    shownRun.current = rid;
    const my = ++readSeq.current, stale = () => shownRun.current !== rid || readSeq.current !== my;
    const started = Date.now();   // (tf3-38)
    setView("run");
    setLoading(true); setErr(null); setMsg(null);
    try { const d = await opsApi.runSteps(rid); if (stale()) return; setData(d); }   // (audit 10-21 P10) another run opened since: dropped
    catch (e) { if (stale()) return; setErr(String((e as Error).message)); setData(null); }
    finally { if (!stale()) setLoading(false); }
    // The filmstrip fails on its own: the step table below is the inspector's fallback.
    try { const f = await opsApi.filmstrip(rid); if (stale()) return; setFilm(f); setFilmErr(null); setReadAt(started); }
    catch (e) { if (stale()) return; setFilm(null); setFilmErr(String((e as Error).message)); }
  }, [runId]);
  // (tf3-38) after an edit, the run is read again every 10 s — quietly (the page's message stays) — until the gate parks
  //  again or the check holds it again
  const reread = useCallback(async (rid: string) => {
    const started = Date.now();
    try { const [d, f] = await Promise.all([opsApi.runSteps(rid), opsApi.filmstrip(rid)]); if (shownRun.current !== rid) return; setData(d); setFilm(f); setReadAt(started); }
    catch { /* catch-reason: the next tick reads again; the page keeps what it last read */ }
  }, []);
  const filmRunId = film?.run.id ?? null;
  const where = film ? atStoryboard(film.run.awaitingGate?.stepId, data?.steps ?? []) : null;
  const parked = film?.run.awaitingGate?.stepId === "gate_storyboard";
  const pending = !!edited && edited.runId === filmRunId;
  const hideApprove = pending && approveHidden(edited!.at, readAt, parked);
  useEffect(() => {
    if (!edited) return;
    if (edited.runId !== filmRunId || (readAt > edited.at && where !== null)) { setEdited(null); return; }   // parked again, or held again
    const t = setInterval(() => {
      if (rereadDone(edited.at, Date.now())) { setEdited(null); return; }   // (audit 10-21 F3) not for ever
      void reread(edited.runId);
    }, REREAD_MS);
    return () => clearInterval(t);
  }, [edited, filmRunId, readAt, where, reread]);

  // jump straight into a run from the fleet needs-attention list (click a run id)
  const openRun = useCallback((rid: string) => { setRunId(rid); load(rid); }, [load]);
  // Opened with ?run=<id> (the Operations page's run links): show that run straight away.
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("run");
    if (rid && /^[0-9a-fA-F-]{36}$/.test(rid)) openRun(rid);
  }, [openRun]);

  const act = async (key: string, fn: () => Promise<{ ok: boolean; reason: string }>) => {
    setBusy(key); setMsg(null);
    try {
      const r = await fn();
      setMsg((r.ok ? "✓ " : "✗ ") + r.reason);
      if (view === "run") await load();
    } catch (e) { setMsg("✗ " + (e as Error).message); }
    finally { setBusy(null); }
  };

  // G5 Re-roll the LAYER SHOWN: a frame → the bounded review regenerate (never past the storyboard gate); a clip →
  // retake take 0 and the takes chained after it.
  const reroll = (rid: string, b: FilmstripBeat, layer: "frame" | "clip") =>
    act(`reroll-${b.beatId}`, () => (layer === "frame" ? opsApi.regenerateFrame(rid, b.beatId) : opsApi.retakeTake(rid, b.beatId, 0)));

  const steps = (data?.steps ?? []).filter((s) =>
    (!statusFilter || s.status === statusFilter) && (!beatFilter || (s.beatId ?? "") === beatFilter));
  const beats = [...new Set((data?.steps ?? []).map((s) => s.beatId).filter(Boolean))] as string[];
  const statuses = [...new Set((data?.steps ?? []).map((s) => s.status))];

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Run Inspector</h1>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Find and fix broken runs — retry, skip, re-render, re-stitch, revive — without touching mysql.
      </p>

      {/* page-level view switch: fleet-wide problems (the landing) vs a specific run */}
      <div style={{ display: "flex", gap: 6, margin: "14px 0 12px" }}>
        <button onClick={() => setView("attention")}
          style={{ ...btnPrimary, background: view === "attention" ? "#111827" : "#fff", color: view === "attention" ? "#fff" : "#111827", border: view === "attention" ? 0 : "1px solid #d1d5db" }}>
          ⚠ Needs attention (all runs)
        </button>
        <button onClick={() => setView("run")}
          style={{ ...btnPrimary, background: view === "run" ? "#111827" : "#fff", color: view === "run" ? "#fff" : "#111827", border: view === "run" ? 0 : "1px solid #d1d5db" }}>
          Inspect a specific run
        </button>
        <button onClick={() => setView("completed")}
          style={{ ...btnPrimary, background: view === "completed" ? "#111827" : "#fff", color: view === "completed" ? "#fff" : "#111827", border: view === "completed" ? 0 : "1px solid #d1d5db" }}>
          ✓ Completed jobs → post
        </button>
      </div>

      {view === "completed" && (
        <div>
          <p style={{ color: "#6b7280", fontSize: 12.5, marginTop: 0 }}>
            Successfully-completed runs and their final video — the handoff list for finishing. Open a run to re-stitch or inspect before handing it off.
          </p>
          <CompletedJobs openRun={openRun} />
        </div>
      )}

      {view === "attention" && (
        <div>
          <p style={{ color: "#6b7280", fontSize: 12.5, marginTop: 0 }}>
            Every failed step across all runs the self-healing loop won't fix on its own. Retry or skip inline, or open the run to dig in.
          </p>
          <FleetNeedsAttention busy={busy} act={act} openRun={openRun} />
        </div>
      )}

      {view === "run" && (
      <>
      <div style={{ display: "flex", gap: 8, margin: "0 0 14px", alignItems: "center" }}>
        <input value={runId} onChange={(e) => setRunId(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="paste a run id (36-char uuid), or click one from Needs attention"
          style={{ flex: 1, maxWidth: 480, padding: 8, borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "monospace" }} />
        <button style={btnPrimary} onClick={() => load()} disabled={loading}>{loading ? "Loading…" : "Inspect"}</button>
      </div>

      {err && <div style={{ color: "#b91c1c", padding: 10 }}>{err}</div>}
      {!data && !loading && !err && (
        <div style={{ color: "#9ca3af", padding: "20px 4px", fontSize: 13 }}>
          Paste a run id above, or switch to <strong>Needs attention</strong> to find broken runs and click into one.
        </div>
      )}
      {msg && <div style={{ padding: "8px 10px", borderRadius: 6, background: msg.startsWith("✓") ? "#f0fdf4" : "#fef2f2", color: msg.startsWith("✓") ? "#166534" : "#b91c1c", fontSize: 13, marginBottom: 10 }}>{msg}</div>}

      {data?.run && (
        <>
          {/* run summary + run-level actions */}
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 14, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{data.run.workflowId}</span>
                <span style={{ marginLeft: 10, padding: "2px 8px", borderRadius: 5, fontSize: 12, background: (STATUS_COLOR[data.run.status] ?? "#6b7280") + "22", color: STATUS_COLOR[data.run.status] ?? "#6b7280" }}>{data.run.status}</span>
                <span style={{ color: "#9ca3af", marginLeft: 10 }}>{data.stepCount} steps</span>
                {data.run.dryRunBeforeClips && <span style={{ marginLeft: 10, padding: "2px 8px", borderRadius: 5, fontSize: 12, background: "#eef2ff", color: "#3730a3" }}>dry run</span>}
                {data.run.videoId && <span style={{ color: "#9ca3af", marginLeft: 10 }}>video {data.run.videoId.slice(0, 8)}</span>}
                {/* (session 9) the order was accepted WITH these — shown so a degraded intake is never invisible */}
                {(data.run.intakeWarnings?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "#fffbeb", border: "1px solid #fcd34d", color: "#92400e", fontSize: 12 }}>
                    <b>Intake warnings ({data.run.intakeWarnings!.length})</b>
                    <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>{data.run.intakeWarnings!.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btnWarn} disabled={busy === "restitch"} onClick={() => act("restitch", () => opsApi.reStitch(data.run!.id))}>Re-stitch final</button>
                <button style={btn} disabled={busy === "revive"} onClick={() => act("revive", () => opsApi.revive(data.run!.id))}>Revive run</button>
                {/* (B105) the orchestrator's cancel, with its reason — the one that stops a run with a step in flight */}
                <button style={btnDanger} disabled={busy === "abandon"} onClick={() => {
                  const why = window.prompt(ABANDON_ASK)?.trim();
                  if (why) act("abandon", async () => { const r = await opsOverviewApi.cancelRun(data.run!.id, why); return { ok: r.ok, reason: r.reason ?? (r.ok ? "cancelled" : "not cancelled") }; });
                }}>Abandon run</button>
                {/* everything about this run in one file — to send for a diagnosis, no SQL (built + redacted by the workers) */}
                <button style={btn} disabled={busy === "bundle"} title="the run, every step and its failure reason, gates, run events, the filmstrip, the audit spans, the failed calls' prompts and replies — secrets redacted"
                  onClick={() => act("bundle", async () => {
                    const b = await opsOverviewApi.debugBundle(data.run!.id, "failed");
                    const url = URL.createObjectURL(new Blob([JSON.stringify(b, null, 2)], { type: "application/json" }));
                    const a = document.createElement("a"); a.href = url; a.download = `run-debug-${data.run!.id.slice(0, 8)}.json`; a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                    return { ok: true } as never;
                  })}>⬇ Debug bundle</button>
                {/* (session 9) automatic captures: certification and failed runs are captured on their own; take one now, or
                    download the latest (a signed link; the download is recorded with who) */}
                <button style={btn} disabled={busy === "capture"} title="store this run's full capture now (kept 30 days)"
                  onClick={() => act("capture", () => opsOverviewApi.takeCapture(data.run!.id) as never)}>📌 Capture now</button>
                <button style={btn} disabled={busy === "capdl"} title="download the latest stored capture (recorded with who)"
                  onClick={() => act("capdl", async () => {
                    const { captures } = await opsOverviewApi.captures(data.run!.id);
                    if (!captures.length) { alert("No capture for this run yet — take one with Capture now."); return { ok: true } as never; }
                    const link = await opsOverviewApi.captureDownload(captures[0]!.id);
                    window.open(link.url, "_blank", "noopener");
                    return { ok: true } as never;
                  })}>⬇ Latest capture</button>
              </div>
            </div>
          </div>

          {/* (B105) an ended run says why — and no gate it once waited at is offered below */}
          {endReasonLine(data.run.status, data.steps) && <div style={{ padding: "8px 10px", borderRadius: 6, background: "#f9fafb", border: "1px solid #e5e7eb", fontSize: 13, marginBottom: 10 }}>{endReasonLine(data.run.status, data.steps)}</div>}
          {/* (tf3-35b) a dry run that stopped before its first clip: parked until cancelled (capture it first if needed) */}
          {isDryRunStopped({ run: data.run }) && <DryRunStopped runId={data.run.id} onCancelled={() => void load(data.run!.id)} />}
          {/* (B119; C2.22) a rehearsal: stand-in clips, never a deliverable or a credit */}
          {isRehearsal({ run: data.run }) && <RehearsalBadge run={data.run} onCancelled={() => void load(data.run!.id)} />}

          {/* G5: pictures first — the contact sheet when parked at the storyboard gate, else the filmstrip */}
          {filmErr && <div style={{ color: "#b45309", fontSize: 12, marginBottom: 8 }}>filmstrip unavailable: {filmErr}</div>}
          {/* (tf3-38) the rules card, at the gate or the check's hold (and while an edit's re-run is under way) */}
          {film && (where || pending) && !runEnded(data.run.status) && <RulesCardPanel key={film.run.id} runId={film.run.id} where={where ?? "gate"}
            onEdited={(at) => { setEdited({ runId: film.run.id, at }); void reread(film.run.id); }} />}
          {hideApprove && <div style={{ color: "#92400e", fontSize: 12, margin: "4px 0" }}>The rules card was edited: the storyboard gate is back
            to waiting while it re-runs. Approve comes back when the gate parks again (read every 10 s).</div>}
          {film && (film.run.awaitingGate?.stepId === "gate_storyboard" && !hideApprove && !runEnded(data.run.status)
            ? <ContactSheet film={film} marks={marks} deciding={busy === "gate"}
                onMark={(b, m) => setMarks((x) => ({ ...x, [b]: m }))}
                onReroll={(b, layer) => reroll(film.run.id, b, layer)}
                onDecide={(approved, note) => {
                  if (!approved && !confirm("Reject the storyboard gate? The run will be failed off.")) return;
                  act("gate", () => opsApi.decideGate(film.run.awaitingGate!.rowId, approved, note));
                }} />
            : <FilmstripView film={film} busy={!!busy} marks={marks}
                onMark={(b, m) => setMarks((x) => ({ ...x, [b]: m }))}
                onReroll={(b, layer) => reroll(film.run.id, b, layer)}
                more={(b) => [
                  { label: "Re-render beat", run: () => act(`rr-${b.beatId}`, () => opsApi.reRenderBeat(film.run.id, b.beatId)) },
                  { label: "Cascade from clip", run: () => act(`cc-${b.beatId}`, () => opsApi.cascade(film.run.id, "generate_clip", b.beatId)) },
                  // Session 6 T2: a SOFT drop — off the video, recorded and restorable; frames and clips are kept
                  { label: "Drop beat", run: () => {
                    if (!confirm(`Drop ${b.beatId}? It leaves the video; the voice is re-conformed without it and every other beat keeps its audio. Its frames and clips are kept, and it can be restored.`)) return;
                    act(`drop-${b.beatId}`, () => opsApi.dropBeat(film.run.id, b.beatId));
                  } },
                ]} />)}
          {film?.run.awaitingGate?.stepId === "gate_compare" && !runEnded(data.run.status) && <OperatorCompare runId={film.run.id} busy={!!busy}
            act={(key, fn) => act(key, fn)} />}
          {/* (TF-6b-4) a gate with no screen of its own here: the generic decision, through the row-keyed decide */}
          {film?.run.awaitingGate && !runEnded(data.run.status) && <AdminGateNotes key={film.run.id} runId={film.run.id} stepId={film.run.awaitingGate.stepId} />}
          {film?.run.awaitingGate && !runEnded(data.run.status) && genericDecide(film.run.awaitingGate.stepId) && <GateDecideBox stepId={film.run.awaitingGate.stepId} busy={busy === "gate"}
            decide={(approved, note) => act("gate", () => opsApi.decideGate(film.run.awaitingGate!.rowId, approved, note))}>
            {film.run.awaitingGate.stepId === "gate_screenplay" ? <AdminScreenplay key={film.run.id} runId={film.run.id} /> : undefined}
          </GateDecideBox>}
          {/* (TF-6b-1) the question the run waits on, answered here by the run's own account */}
          {film && <AdminNeedsYou key={film.run.id} runId={film.run.id} />}
          {/* (B112; C2.5-4) what the customer is told changed at gate_final: the run's material rows, with their steps and spans */}
          {film && <AdminMaterialRows key={`m-${film.run.id}`} runId={film.run.id} />}
          {film && <DroppedBeats runId={film.run.id} busy={!!busy} restore={(beatId) => act(`restore-${beatId}`, () => opsApi.restoreBeat(film.run.id, beatId))} />}
          {film && <RetentionCurves cmsId={film.cmsId} />}

          <details open={!film}>
          <summary style={{ cursor: "pointer", fontSize: 13, margin: "6px 0 10px" }}>Steps ({data.stepCount})</summary>
          {/* tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {(["steps", "attention"] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ ...btn, background: tab === t ? "#111827" : "#fff", color: tab === t ? "#fff" : "#111827" }}>
                {t === "steps" ? "All steps" : "Needs attention"}
              </button>
            ))}
          </div>

          {tab === "steps" ? (
            <>
              {/* filters */}
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...btn, cursor: "default" }}>
                  <option value="">all statuses</option>
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={beatFilter} onChange={(e) => setBeatFilter(e.target.value)} style={{ ...btn, cursor: "default" }}>
                  <option value="">all beats</option>
                  {beats.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <span style={{ color: "#9ca3af", fontSize: 12, alignSelf: "center" }}>{steps.length} shown</span>
              </div>
              <StepsTable steps={steps} run={data.run} busy={busy} act={act} />
            </>
          ) : (
            <NeedsAttention runId={data.run.id} busy={busy} act={act} />
          )}
          </details>
        </>
      )}
      </>
      )}
    </div>
  );
}

function StepsTable({ steps, run, busy, act }: {
  steps: RunStep[]; run: NonNullable<RunInspector["run"]>;
  busy: string | null; act: (k: string, fn: () => Promise<{ ok: boolean; reason: string }>) => void;
}) {
  const [openBeat, setOpenBeat] = useState<string | null>(null);
  if (steps.length === 0) return <div style={{ color: "#9ca3af", padding: 20 }}>No steps match.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
      <thead>
        <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
          <Th>step</Th><Th>beat</Th><Th>status</Th><Th>attempt</Th><Th>failure</Th><Th>duration</Th><Th>actions</Th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => (
          <>
            <tr key={s.rowId} style={{ borderBottom: "1px solid #f3f4f6", background: s.status === "failed" ? "#fef2f2" : undefined }}>
              <Td><code>{s.stepId}</code></Td>
              <Td>{s.beatId ? <button style={{ ...btn, padding: "2px 6px" }} onClick={() => setOpenBeat(openBeat === s.beatId ? null : s.beatId)}>{s.beatId}</button> : <span style={{ color: "#9ca3af" }}>—</span>}</Td>
              <Td><span style={{ padding: "2px 7px", borderRadius: 5, background: (STATUS_COLOR[s.status] ?? "#6b7280") + "22", color: STATUS_COLOR[s.status] ?? "#6b7280" }}>{s.status}</span></Td>
              <Td>{s.attemptNumber}</Td>
              <Td style={{ color: "#b91c1c", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} >{s.failureCategory ?? "—"}{s.failureCategory && HOLD_WORDS[s.failureCategory] ? ` — ${HOLD_WORDS[s.failureCategory]}` : ""}{s.respecificationReason ? ` · ${s.respecificationReason}` : ""}
                {s.status === "failed" && s.failureCategory && HOLD_REMEDY[s.failureCategory] && <div style={{ whiteSpace: "normal", color: "#92400e", fontSize: 12 }}>{HOLD_REMEDY[s.failureCategory]}</div>}</Td>
              <Td>{dur(s.durationSec)}</Td>
              <Td>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <button style={btn} disabled={!!busy} title="retry this step" onClick={() => act(`retry-${s.rowId}`, () => opsApi.retryStep(s.rowId))}>Retry</button>
                  <button style={btnWarn} disabled={!!busy} title="skip this step" onClick={() => { const r = prompt("Skip reason (optional):") ?? undefined; act(`skip-${s.rowId}`, () => opsApi.skipStep(s.rowId, r)); }}>Skip</button>
                  <button style={btn} disabled={!!busy} title="re-render from here (cascade downstream)" onClick={() => act(`casc-${s.rowId}`, () => opsApi.cascade(run.id, s.stepId, s.beatId ?? undefined))}>Re-render↓</button>
                  {s.beatId && <button style={btn} disabled={!!busy} title="re-render this whole beat" onClick={() => act(`beat-${s.rowId}`, () => opsApi.reRenderBeat(run.id, s.beatId!))}>Re-render beat</button>}
                </div>
              </Td>
            </tr>
            {openBeat && openBeat === s.beatId && (
              <tr key={s.rowId + "-art"}><td colSpan={7} style={{ padding: 0 }}><ArtefactViewer cmsId={run.cmsId} beatId={s.beatId} /></td></tr>
            )}
          </>
        ))}
      </tbody>
    </table>
  );
}

function ArtefactViewer({ cmsId, beatId }: { cmsId: string | null; beatId: string }) {
  const [prod, setProd] = useState<BeatProduction | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!cmsId) return;
    opsApi.beatProduction(cmsId, beatId).then(setProd).catch((e) => setErr(String((e as Error).message)));  // catch-reason: the error is shown to the person (setErr)
  }, [cmsId, beatId]);
  if (!cmsId) return <div style={{ padding: 10, color: "#9ca3af", fontSize: 12 }}>no cmsId on this run — artefacts unavailable</div>;
  return (
    <div style={{ padding: 12, background: "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
      {err ? <span style={{ color: "#9ca3af", fontSize: 12 }}>{err}</span> : !prod ? <span style={{ color: "#9ca3af", fontSize: 12 }}>loading artefacts…</span> : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", fontSize: 12 }}>
          {prod.clip?.url && <div><video src={prod.clip.url} controls style={{ width: 160, borderRadius: 6 }} /><div style={{ color: "#6b7280" }}>clip · {prod.clip.status}</div></div>}
          {prod.image?.url && <div><img src={prod.image.url} alt="" style={{ width: 120, borderRadius: 6 }} /><div style={{ color: "#6b7280" }}>image · {prod.image.status}</div></div>}
          {prod.card?.url && <div><img src={prod.card.url} alt="" style={{ width: 120, borderRadius: 6 }} /><div style={{ color: "#6b7280" }}>card · {prod.card.status}</div></div>}
          {(prod.clipDurationSec != null && prod.resolvedDurationSec != null && prod.clipDurationSec !== prod.resolvedDurationSec) && (
            <div style={{ color: "#b45309" }}>⚠ duration mismatch: clip {prod.clipDurationSec}s vs resolved {prod.resolvedDurationSec}s</div>
          )}
          {!prod.clip?.url && !prod.image?.url && !prod.card?.url && <span style={{ color: "#9ca3af" }}>no artefacts produced yet</span>}
        </div>
      )}
    </div>
  );
}

function CompletedJobs({ openRun }: { openRun: (runId: string) => void }) {
  const [jobs, setJobs] = useState<CompletedJob[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [withVideoOnly, setWithVideoOnly] = useState(true);
  // Which completed run's Finish & Post panel is open (the §13 post-production seam).
  const [postRun, setPostRun] = useState<string | null>(null);

  const reload = useCallback(() => {
    setErr(null); setJobs(null);
    opsApi.completedRuns({ withVideoOnly, limit: 200 }).then(setJobs).catch((e) => setErr(String((e as Error).message)));  // catch-reason: the error is shown to the person (setErr)
  }, [withVideoOnly]);
  useEffect(() => { reload(); }, [reload]);

  const copy = (t: string) => { navigator.clipboard?.writeText(t); };

  if (err) return <ErrorBox msg={err} onRetry={reload} />;
  if (!jobs) return <Loading />;

  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#374151", marginBottom: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={withVideoOnly} onChange={(e) => setWithVideoOnly(e.target.checked)} />
        only jobs with a final video (ready to hand off)
      </label>
      {jobs.length === 0 ? (
        <Empty msg={withVideoOnly ? "No completed jobs with a final video yet." : "No completed jobs yet."} />
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
              <Th>run</Th><Th>workflow</Th><Th>final video</Th><Th>completed</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {jobs.flatMap((j) => [
              <tr key={j.runId} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <Td>
                  <code style={{ fontSize: 12 }}>{j.runId.slice(0, 8)}…</code>
                  <button style={{ ...btn, padding: "1px 6px", marginLeft: 6 }} title="copy full run id" onClick={() => copy(j.runId)}>copy</button>
                </Td>
                <Td>{j.workflowId}</Td>
                <Td>
                  {j.finalVideoUrl
                    ? <a href={j.finalVideoUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>open ↗</a>
                    : <span style={{ color: "#d97706" }}>no url ({j.videoStatus ?? "?"})</span>}
                </Td>
                <Td style={{ color: "#9ca3af" }}>{j.endedAt ? new Date(j.endedAt).toLocaleString() : "—"}</Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  <button style={{ ...btn, padding: "3px 9px" }} onClick={() => openRun(j.runId)}>Inspect →</button>
                  <button
                    style={{ ...btnPrimary, padding: "3px 9px", marginLeft: 6 }}
                    // (session 10, A3: session 8's F1) another agency's run is finished by that agency, never from here
                    disabled={finishingSide(j.tenantId) === "agency"}
                    title={finishingSide(j.tenantId) === "agency" ? "finished by its agency" : undefined}
                    onClick={() => setPostRun(postRun === j.runId ? null : j.runId)}
                  >
                    {postRun === j.runId ? "Close" : "Finish & Post"}
                  </button>
                </Td>
              </tr>,
              postRun === j.runId ? (
                <tr key={`${j.runId}-post`}>
                  <td colSpan={5} style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb", padding: 0 }}>
                    {/* The §13 post-production Finish & Post panel, run-keyed. */}
                    <FinishAndPostPanel productionRunId={j.runId} tenantId={j.tenantId} />
                  </td>
                </tr>
              ) : null,
            ])}
          </tbody>
        </table>
      )}
      <div style={{ color: "#9ca3af", fontSize: 11.5, marginTop: 8 }}>{jobs.length} completed job{jobs.length === 1 ? "" : "s"} shown (newest first, max 200).</div>
    </div>
  );
}

function FleetNeedsAttention({ busy, act, openRun }: {
  busy: string | null;
  act: (k: string, fn: () => Promise<{ ok: boolean; reason: string }>) => void;
  openRun: (runId: string) => void;
}) {
  const [items, setItems] = useState<NeedsAttentionItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = useCallback(() => {
    setErr(null); setItems(null);
    opsApi.needsAttention().then(setItems).catch((e) => setErr(String((e as Error).message)));  // catch-reason: the error is shown to the person (setErr)
  }, []);
  useEffect(() => { reload(); }, [reload]);
  if (err) return <ErrorBox msg={err} onRetry={reload} />;
  if (!items) return <Loading />;
  if (items.length === 0) return <div style={{ color: "#16a34a", padding: 20, fontSize: 14 }}>✓ Nothing needs attention across any run. The factory is healthy.</div>;

  // group by run, then show a per-run summary with an "open" link + inline actions.
  const byRun = new Map<string, NeedsAttentionItem[]>();
  for (const it of items) { const arr = byRun.get(it.runId) ?? byRun.set(it.runId, []).get(it.runId)!; arr.push(it); }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, color: "#6b7280" }}>{items.length} failed step{items.length === 1 ? "" : "s"} across {byRun.size} run{byRun.size === 1 ? "" : "s"} — live runs, and runs that failed in the last 48 h. Cancelled, finished and older runs aren't listed (older failures: Operations → Runs → Failing).</div>
      {[...byRun].map(([rid, list]) => {
        const cats = [...new Set(list.map((i) => i.failureCategory ?? "uncategorized"))];
        return (
          <div key={rid} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 13 }}>
                {/* what this run IS — its workflow and age — not only its id (2026-09-29) */}
                <b>{list[0]?.run?.workflowId ?? "run"}</b>
                <code style={{ fontSize: 12, marginLeft: 8, color: "#6b7280" }}>{rid.slice(0, 8)}…</code>
                {list[0]?.run?.createdAt && <span style={{ color: "#6b7280", marginLeft: 8 }}>started {new Date(list[0].run.createdAt).toLocaleString()}</span>}
                <span style={{ color: "#b91c1c", marginLeft: 10 }}>{list.length} failed</span>
                <span style={{ color: "#9ca3af", marginLeft: 10 }}>{cats.join(", ")}</span>
              </div>
              <button style={{ ...btnPrimary, padding: "4px 10px" }} onClick={() => openRun(rid)}>Open run →</button>
            </div>
            <div style={{ marginTop: 8 }}><RunThumbs runId={rid} max={8} height={80} onOpen={() => openRun(rid)} /></div>
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
              {list.slice(0, 6).map((it) => (
                <div key={it.rowId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
                  <span><code>{it.stepId}</code>{it.beatId ? ` · ${it.beatId}` : ""} · attempt {it.attemptNumber}</span>
                  <div style={{ display: "flex", gap: 5 }}>
                    <button style={btn} disabled={!!busy} onClick={() => act(`retry-${it.rowId}`, () => opsApi.retryStep(it.rowId))}>Retry</button>
                    <button style={btnWarn} disabled={!!busy} onClick={() => { const r = prompt("Skip reason (optional):") ?? undefined; act(`skip-${it.rowId}`, () => opsApi.skipStep(it.rowId, r)); }}>Skip</button>
                  </div>
                </div>
              ))}
              {list.length > 6 && <div style={{ fontSize: 12, color: "#9ca3af" }}>+{list.length - 6} more — open the run to see all.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NeedsAttention({ runId, busy, act }: {
  runId: string; busy: string | null; act: (k: string, fn: () => Promise<{ ok: boolean; reason: string }>) => void;
}) {
  const [items, setItems] = useState<NeedsAttentionItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    opsApi.needsAttention(runId).then(setItems).catch((e) => setErr(String((e as Error).message)));  // catch-reason: the error is shown to the person (setErr)
  }, [runId]);
  if (err) return <div style={{ color: "#b91c1c", padding: 10 }}>{err}</div>;
  if (!items) return <div style={{ color: "#9ca3af", padding: 20 }}>Loading…</div>;
  if (items.length === 0) return <div style={{ color: "#16a34a", padding: 20 }}>✓ Nothing needs attention in this run.</div>;
  // group by failure category
  const byCat = new Map<string, NeedsAttentionItem[]>();
  for (const it of items) { const c = it.failureCategory ?? "uncategorized"; (byCat.get(c) ?? byCat.set(c, []).get(c)!).push(it); }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {[...byCat].map(([cat, list]) => (
        <div key={cat} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{cat} <span style={{ color: "#9ca3af" }}>({list.length})</span></div>
          {list.map((it) => (
            <div key={it.rowId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 12 }}>
              <span><code>{it.stepId}</code>{it.beatId ? ` · ${it.beatId}` : ""} · attempt {it.attemptNumber}</span>
              <div style={{ display: "flex", gap: 5 }}>
                <button style={btn} disabled={!!busy} onClick={() => act(`retry-${it.rowId}`, () => opsApi.retryStep(it.rowId))}>Retry</button>
                <button style={btnWarn} disabled={!!busy} onClick={() => { const r = prompt("Skip reason (optional):") ?? undefined; act(`skip-${it.rowId}`, () => opsApi.skipStep(it.rowId, r)); }}>Skip</button>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const Th = ({ children }: { children?: React.ReactNode }) => <th style={{ padding: "8px 10px", fontWeight: 600 }}>{children}</th>;
const Td = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => <td style={{ padding: "6px 10px", ...style }}>{children}</td>;
const Loading = () => <div style={{ color: "#9ca3af", padding: 20 }}>Loading…</div>;
const Empty = ({ msg }: { msg: string }) => <div style={{ color: "#9ca3af", padding: 20 }}>{msg}</div>;
const ErrorBox = ({ msg, onRetry }: { msg: string; onRetry: () => void }) => (
  <div style={{ color: "#b91c1c", padding: 20 }}>
    {msg} <button style={{ ...btn, marginLeft: 8 }} onClick={onRetry}>retry</button>
  </div>
);


/** Session 6 T2: the run's DROPPED beats — off the video (and the filmstrip above), recorded, restorable. Hidden when none. */
function DroppedBeats(p: { runId: string; busy: boolean; restore: (beatId: string) => Promise<void> | void }) {
  const [beats, setBeats] = useState<{ beatId: string; name: string | null; dropped: { at: string; by: string | null } }[]>([]);
  const [readErr, setReadErr] = useState<string | null>(null);
  // (tf3-31, A17) a failed read is shown, never "no dropped beats"
  const load = useCallback(() => { setReadErr(null); opsApi.droppedBeats(p.runId).then((r) => setBeats(r.beats))
    .catch((e) => setReadErr(String((e as Error)?.message ?? e))); }, [p.runId]);   // catch-reason: the error is shown in place of the list
  useEffect(() => { load(); }, [load, p.busy]);
  if (readErr) return <ErrorBox msg={`The dropped beats couldn't be read: ${readErr}`} onRetry={load} />;
  if (beats.length === 0) return null;
  return (
    <div style={{ marginTop: 12, padding: 10, border: "1px dashed #d1d5db", borderRadius: 6, fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Dropped beats</div>
      {beats.map((b) => (
        <div key={b.beatId} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <span><code>{b.beatId}</code>{b.name ? ` · ${b.name}` : ""}</span>
          <span style={{ color: "#6b7280" }}>dropped {new Date(b.dropped.at).toLocaleString()}{b.dropped.by ? ` by ${b.dropped.by}` : ""}</span>
          <button disabled={p.busy} onClick={async () => { await p.restore(b.beatId); load(); }}>Restore</button>
        </div>
      ))}
    </div>
  );
}


/** Session 6 T3: the operator's comparison at gate_compare — the executions with their cost; pick, or make a new round. */
function OperatorCompare(p: { runId: string; busy: boolean; act: (key: string, fn: () => Promise<{ ok: boolean; reason: string }>) => Promise<void> | void }) {
  const [data, setData] = useState<import("../../components/engine/CompareExecutions").CompareData | null>(null);
  const [readErr, setReadErr] = useState<string | null>(null);
  // (tf3-31, A17) a failed read is shown, never a missing comparison
  const load = useCallback(() => { setReadErr(null); opsApi.compare(p.runId).then(setData)
    .catch((e) => { setData(null); setReadErr(String((e as Error)?.message ?? e)); }); }, [p.runId]);   // catch-reason: the error is shown in place of the comparison
  useEffect(() => { load(); }, [load, p.busy]);
  if (readErr) return <ErrorBox msg={`The comparison couldn't be read: ${readErr}`} onRetry={load} />;
  if (!data) return null;
  return (
    <div style={{ margin: "12px 0", padding: 12, border: "1px solid #c7d2fe", background: "#eef2ff", borderRadius: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Compare executions{data.round ? ` — round ${data.round}` : ""}</div>
      <CompareExecutions data={data} operator busy={p.busy}
        onChoose={(k) => p.act(`compare-${k}`, () => opsApi.compareChoose(p.runId, k))}
        onReject={() => p.act("compare-reject", () => opsApi.compareReject(p.runId))} />
    </div>
  );
}
